import React, { useEffect, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import CustomColorPicker from "./CustomColorPicker";

const useStyles = createUseStyles({
  colorPicker: {
    position: "absolute",
    left: "55px",
    display: "flex",
    zIndex: 10000,
  },
  colorPickerButtonWrapper: {},
  colorPickerButton: {
    height: "28px",
    width: "28px",
    boxShadow: "0px 0px 0px 0.7px #E0E0E0",
    borderRadius: "39.2px",
    margin: "4px",
  },
});

const decimalToHex = (alpha) => {
  const parsed = Number(alpha);
  const normalized = Number.isFinite(parsed) ? parsed : 1;
  const clamped = Math.max(0, Math.min(1, normalized));
  return Math.round(clamped * 255).toString(16).padStart(2, "0");
};

const hexToRgba = (value) => {
  if (typeof value !== "string") {
    return { r: 0, g: 0, b: 0, a: 1 };
  }

  const hex = value.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(hex)) {
    return { r: 0, g: 0, b: 0, a: 1 };
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;

  return { r, g, b, a };
};

const rgbaToHex = ({ r, g, b, a }) => {
  const toHex = (value) =>
    Math.max(0, Math.min(255, Number(value) || 0))
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}${decimalToHex(a)}`;
};

export const ColorPicker = ({
  open,
  color,
  onSave,
  onClose,
  templateColors = [],
}) => {
  const [current, setCurrent] = useState(() => hexToRgba(color));
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClose(event);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [onClose]);

  useEffect(() => {
    setCurrent(hexToRgba(color));
  }, [color]);

  const classes = useStyles();
  return (
    open && (
      <div className={classes.colorPicker} ref={ref}>
        <CustomColorPicker
          color={current}
          onChange={(c) => {
            setCurrent({
              r: c.rgb.r,
              g: c.rgb.g,
              b: c.rgb.b,
              a: c.rgb.a,
            });
          }}
          onChangeComplete={(c) => onSave(rgbaToHex(c.rgb))}
          templateColors={templateColors}
        />
      </div>
    )
  );
};

export const ColorPickerButton = ({ color, onChange, templateColors }) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className={classes.colorPickerButtonWrapper}>
      <div
        className={classes.colorPickerButton}
        style={{ backgroundColor: color }}
        onClick={() => setIsOpen(true)}
      />
      <ColorPicker
        open={isOpen}
        onClose={() => setIsOpen(false)}
        color={color}
        onSave={onChange}
        templateColors={templateColors}
      />
    </div>
  );
};
